// Lightweight UI primitives matching the Valexos dashboard look.
// Kept in one file so the page code stays readable and there's no scattering
// of one-component-per-file. Add a separate file when a component grows past
// ~80 lines.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ── Panel ────────────────────────────────────────────────────────────────
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-[10px] border border-line bg-bg-1', className)}>
      {children}
    </div>
  );
}

export function PanelHead({
  title,
  sub,
  right,
}: {
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-[18px] py-[14px]">
      <span className="text-[11px] font-bold uppercase tracking-eyebrow text-text">
        {title}
        {sub && (
          <span className="ml-2 text-[11px] font-normal normal-case tracking-wide text-text-dim">
            {sub}
          </span>
        )}
      </span>
      {right}
    </div>
  );
}

export function PanelBody({
  children,
  flush = false,
  className,
}: {
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return <div className={cn(flush ? 'p-0' : 'p-[18px]', className)}>{children}</div>;
}

// ── KPI card ─────────────────────────────────────────────────────────────
type KpiTone = 'green' | 'blue' | 'amber' | 'red';
const toneBar: Record<KpiTone, string> = {
  green: 'bg-green',
  blue:  'bg-blue',
  amber: 'bg-amber',
  red:   'bg-red',
};

export function Kpi({
  label,
  value,
  sub,
  tone = 'green',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: KpiTone;
}) {
  return (
    <div className="relative overflow-hidden rounded-[10px] border border-line bg-bg-1 px-[18px] pb-[18px] pt-[16px] transition-transform hover:-translate-y-px">
      <span className={cn('absolute inset-y-0 left-0 w-[2px] opacity-70', toneBar[tone])} />
      <div className="mb-2 text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">
        {label}
      </div>
      <div className="num text-2xl font-bold leading-tight tracking-tight">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-text-mid num">{sub}</div>}
    </div>
  );
}

// ── Chips (segmented control) ────────────────────────────────────────────
export function Chips({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex gap-1 rounded-lg border border-line bg-bg-2 p-[3px]">
      {children}
    </div>
  );
}

export function Chip({
  active = false,
  alt = false,
  onClick,
  children,
}: {
  active?: boolean;
  alt?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-md border-none px-3 py-[5px] text-[10.5px] font-semibold uppercase tracking-[0.1em] transition-colors',
        active
          ? alt
            ? 'bg-blue text-white'
            : 'bg-green text-black'
          : 'bg-transparent text-text-mid hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

// ── Live pill (header) ───────────────────────────────────────────────────
export function LivePill({ children = 'LIVE' }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-green">
      <span className="dot-pulse" />
      {children}
    </span>
  );
}

// ── Trade-side and source badges ─────────────────────────────────────────
export function SideBadge({ side }: { side: 'BUY' | 'SELL' }) {
  return (
    <span
      className={cn(
        'inline-block rounded-[3px] px-2 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.1em]',
        side === 'BUY' ? 'bg-green/10 text-green' : 'bg-red/10 text-red',
      )}
    >
      {side}
    </span>
  );
}

export function SourceBadge({ kind }: { kind: 'bot' | 'manual' }) {
  return (
    <span
      className={cn(
        'inline-block rounded-[3px] px-2 py-[1px] text-[9px] font-bold uppercase tracking-[0.1em]',
        kind === 'bot' ? 'bg-blue/10 text-blue' : 'bg-amber/10 text-amber',
      )}
    >
      {kind === 'bot' ? 'BOT' : 'MAN'}
    </span>
  );
}

// ── Distribution bar (used by the position-size chart) ───────────────────
export function DistRow({
  label,
  value,
  ratio,
  positive = true,
}: {
  label: string;
  value: ReactNode;
  ratio: number; // 0..1
  positive?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 text-[11px]">
      <span className="num w-12 text-text-mid">{label}</span>
      <div className="relative h-[18px] flex-1 overflow-hidden rounded-[3px] bg-bg-2">
        <div
          className={cn(
            'h-full rounded-[3px] transition-[width] duration-500',
            positive
              ? 'bg-gradient-to-r from-green-dim to-green'
              : 'bg-gradient-to-r from-red to-[#dc2626]',
          )}
          style={{ width: `${Math.max(2, ratio * 100)}%` }}
        />
      </div>
      <span className="num w-16 text-right text-text">{value}</span>
    </div>
  );
}
